import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';

type ConfigRow = { id: string; name: string; status?: string; current_version?: number; published_version?: number };
type TemplateRow = { template_id: string; name: string; description?: string };

const NEXT_ACTIONS: Record<string, string[]> = {
  draft: ['validate', 'submit'],
  validated: ['submit'],
  submitted: ['review'],
  in_review: ['approve', 'reject'],
  approved: ['publish'],
  published: ['archive'],
  archived: [],
};
const ALL_ACTIONS = ['validate', 'submit', 'review', 'approve', 'publish', 'archive', 'reject'] as const;
const STAGE_INDEX: Record<string, number> = {
  draft: 0,
  validated: 1,
  submitted: 2,
  in_review: 3,
  approved: 4,
  published: 5,
  archived: 6,
};
const ACTION_DONE_AT: Record<string, number> = {
  validate: 1,
  submit: 2,
  review: 3,
  approve: 4,
  publish: 5,
  archive: 6,
  reject: -1,
};

export default function AdminFormConfigsPage() {
  const { activeTenantId, activeConfigId, setActiveConfigId, refresh } = useBusiness();
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [busyConfigId, setBusyConfigId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createMode, setCreateMode] = useState<'blank' | 'template' | 'ai'>('blank');
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [aiMode, setAiMode] = useState<'rules' | 'ai' | 'hybrid'>('hybrid');
  const [aiPrompt, setAiPrompt] = useState('');
  const [creatingNewForm, setCreatingNewForm] = useState(false);

  const load = async () => {
    if (!activeTenantId) return;
    setError('');
    const resp = await apiFetch('/api/form-configs');
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    const data = (await resp.json()) as ConfigRow[];
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    void load();
  }, [activeTenantId]);

  const loadTemplates = async () => {
    const resp = await apiFetch('/api/form-templates', { method: 'GET' });
    if (!resp.ok) return;
    const data = (await resp.json()) as TemplateRow[];
    const arr = Array.isArray(data) ? data : [];
    setTemplates(arr);
    if (!templateId && arr.length > 0) setTemplateId(arr[0].template_id);
  };

  const openCanvas = (configId: string) => {
    setActiveConfigId(configId);
    window.history.pushState({}, '', '/admin/canvas');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const createNewForm = async () => {
    if (!activeTenantId || !name.trim()) return;
    setCreatingNewForm(true);
    setError('');
    setInfo('');
    try {
      let form_schema_json: unknown = { sections: [] };
      let ui_schema_json: unknown = { field_refs: [] };

      if (createMode === 'template' || createMode === 'ai') {
        if (!templateId) {
          setError('Select a template first.');
          return;
        }
        const tResp = await apiFetch(`/api/form-templates/${encodeURIComponent(templateId)}`, { method: 'GET' });
        if (!tResp.ok) {
          setError(await tResp.text());
          return;
        }
        const tpl = await tResp.json() as { form_schema_json?: unknown; ui_schema_json?: unknown };
        form_schema_json = tpl.form_schema_json ?? form_schema_json;
        ui_schema_json = tpl.ui_schema_json ?? ui_schema_json;
      }

      const resp = await apiFetch('/api/form-configs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: activeTenantId,
          name: name.trim(),
          config_type: 'dynamic_form',
          form_schema_json,
          ui_schema_json,
        }),
      });
      if (!resp.ok) {
        setError(await resp.text());
        return;
      }
      const created = (await resp.json()) as { id?: string; config_id?: string };
      const id = String(created.id || created.config_id || '').trim();
      if (!id) {
        setError('Create succeeded but response did not include config id.');
        return;
      }

      if (createMode === 'ai') {
        const genResp = await apiFetch(`/api/form-configs/${encodeURIComponent(id)}/generate-ui-schema?debug=true&mode=${encodeURIComponent(aiMode)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: aiPrompt.trim() || undefined }),
        });
        if (!genResp.ok) {
          setError(await genResp.text());
          return;
        }
      }

      setName('');
      setAiPrompt('');
      setCreateDialogOpen(false);
      await load();
      await refresh();
      openCanvas(id);
    } finally {
      setCreatingNewForm(false);
    }
  };

  const transition = async (id: string, action: string) => {
    setBusyActionId(`${id}:${action}`);
    const resp = await apiFetch(`/api/form-configs/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!resp.ok) {
      setError(await resp.text());
      setBusyActionId(null);
      return;
    }
    await load();
    setInfo(`Action '${action}' completed.`);
    setBusyActionId(null);
  };

  const generateLayout = async (id: string) => {
    setBusyConfigId(id);
    setError('');
    setInfo('');
    const resp = await apiFetch(`/api/form-configs/${id}/generate-ui-schema?debug=true`, { method: 'POST' });
    setBusyConfigId(null);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    try {
      const body = await resp.json() as { warning?: string; debug?: { llm_parse_error?: string; llm_raw_preview?: string; tab_count_after_sanitize?: number } };
      if (body.warning) setInfo(body.warning);
      else setInfo('AI layout generated and saved.');
      if (body.debug?.llm_parse_error) {
        console.info('AI layout parse error:', body.debug.llm_parse_error);
      }
      if (body.debug?.llm_raw_preview) {
        console.info('AI layout raw preview:', body.debug.llm_raw_preview);
      }
      if (typeof body.debug?.tab_count_after_sanitize === 'number') {
        console.info('AI layout tab count:', body.debug.tab_count_after_sanitize);
      }
    } catch {
      setInfo('AI layout generated and saved.');
    }
    await load();
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }}>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }} gutterBottom>
        Form Configs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        Use the <b>Use</b> button in Context column to set active config for Overview/Submissions/Canvas.
      </Typography>
      {!activeTenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a tenant in the Tenants tab before creating or managing configs.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Config name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ minWidth: 260 }}
          disabled={!activeTenantId}
        />
        <Button
          variant="contained"
          onClick={() => {
            setCreateDialogOpen(true);
            void loadTemplates();
          }}
          disabled={!activeTenantId || !name.trim()}
        >
          Create new form
        </Button>
        <Button variant="outlined" onClick={() => void load()}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      <TableContainer sx={{ maxWidth: '100%', maxHeight: 520, overflowY: 'auto', border: '1px solid #e6ebf1', borderRadius: 1 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width="10%">Context</TableCell>
              <TableCell width="22%">Name</TableCell>
              <TableCell width="12%">Status</TableCell>
              <TableCell width="14%">Versions</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const st = (r.status || 'draft').toLowerCase();
              const stage = STAGE_INDEX[st] ?? 0;
              return (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Button
                      size="small"
                      variant={activeConfigId === r.id ? 'contained' : 'outlined'}
                      onClick={() => {
                        setActiveConfigId(r.id);
                        setInfo(`Active config set to: ${r.name}`);
                      }}
                    >
                      {activeConfigId === r.id ? 'Active' : 'Use'}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</TableCell>
                  <TableCell>{st}</TableCell>
                  <TableCell>
                    {r.current_version || 1} / {r.published_version || '-'}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setActiveConfigId(r.id);
                          window.history.pushState({}, '', `/runtime-form/${r.id}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                      >
                        Open Runtime UI
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busyActionId !== null || busyConfigId === r.id}
                        onClick={() => void generateLayout(r.id)}
                      >
                        {busyConfigId === r.id ? 'Generating layout...' : 'Generate Layout (AI)'}
                      </Button>
                      {ALL_ACTIONS.map((a) => {
                        const allowed = (NEXT_ACTIONS[st] || []).includes(a);
                        const done = ACTION_DONE_AT[a] >= 0 && stage >= ACTION_DONE_AT[a];
                        return (
                          <Button
                            key={a}
                            size="small"
                            color={done ? 'success' : (a === 'reject' ? 'error' : 'primary')}
                            variant={done ? 'contained' : 'outlined'}
                            disabled={busyActionId !== null || !allowed}
                            onClick={() => void transition(r.id, a)}
                          >
                            {a}
                          </Button>
                        );
                      })}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {(NEXT_ACTIONS[st] || []).length
                        ? `Next: ${(NEXT_ACTIONS[st] || []).join(' / ')}`
                        : 'No further lifecycle action'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create new form</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Choose how to start. You can always refine in the Canvas.
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              select
              size="small"
              label="Start from"
              value={createMode}
              onChange={(e) => setCreateMode(e.target.value as 'blank' | 'template' | 'ai')}
            >
              <MenuItem value="blank">Blank</MenuItem>
              <MenuItem value="template">Template</MenuItem>
              <MenuItem value="ai">AI (from template)</MenuItem>
            </TextField>

            {(createMode === 'template' || createMode === 'ai') && (
              <TextField
                select
                size="small"
                label="Template"
                fullWidth
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.template_id} value={t.template_id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {createMode === 'ai' && (
              <>
                <TextField
                  select
                  size="small"
                  label="Auto-layout mode"
                  value={aiMode}
                  onChange={(e) => setAiMode(e.target.value as 'rules' | 'ai' | 'hybrid')}
                >
                  <MenuItem value="rules">Rules</MenuItem>
                  <MenuItem value="ai">AI</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Prompt (optional)"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  multiline
                  minRows={3}
                  placeholder="Describe any preferences (e.g., make vitals compact, prioritize required fields)."
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creatingNewForm}>Cancel</Button>
          <Button variant="contained" onClick={() => void createNewForm()} disabled={!activeTenantId || !name.trim() || creatingNewForm}>
            {creatingNewForm ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

